import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import HeroMedia from '@/components/home/HeroMedia';
// Hero image is externalised to Lovable Assets — only the `.asset.json`
// pointer lives on disk. Importing the `.webp` here lets the cdn-pointer
// Vite plugin resolve it to the CDN URL at build time, matching the
// `<link rel="preload">` in index.html.
import ranchStreet from '/images/ranch-street.webp';
// Optional hero video — falls back to poster if unreachable / 503 / 404.
const heroVideoUrl = import.meta.env.VITE_HERO_VIDEO_URL as string | undefined;

const HeroSection = () => {
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const inViewRef = useRef(true);
  // Parallax is desktop-only. On mobile / coarse-pointer / reduced-motion
  // devices we skip the scroll listener and transform writes entirely —
  // mobile CPUs spend a disproportionate amount of TBT on per-frame
  // composited transforms, and the effect is barely visible at small
  // viewports. Computed once at mount; SSR-safe.
  const parallaxEnabled = useRef(false);
  if (typeof window !== 'undefined' && !parallaxEnabled.current) {
    const mqDesktop = window.matchMedia('(min-width: 768px) and (pointer: fine)');
    const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    parallaxEnabled.current = mqDesktop.matches && !mqReduced.matches;
  }

  useEffect(() => {
    if (!parallaxEnabled.current) return;
    // Use IntersectionObserver to track visibility instead of reading
    // getBoundingClientRect() on every scroll (which forces layout/reflow).
    const el = heroRef.current;
    let observer: IntersectionObserver | null = null;
    if (el && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          inViewRef.current = entries[0]?.isIntersecting ?? false;
        },
        { threshold: 0 },
      );
      observer.observe(el);
    }

    const handleScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        if (inViewRef.current) {
          const next = window.scrollY;
          setScrollY((prev) => (prev === next ? prev : next));
        }
        rafRef.current = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (observer) observer.disconnect();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const parallaxOffset = parallaxEnabled.current ? scrollY * 0.4 : 0;
  const counterParallaxOffset = parallaxEnabled.current ? scrollY * -0.1 : 0;

  return (
    <section ref={heroRef} data-hero-section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Parallax Image Background */}
      <div 
        className="absolute inset-0 w-full h-[120%] -top-[10%]"
        style={{
          transform: `translate3d(0, ${parallaxOffset}px, 0)`,
          willChange: 'transform',
        }}
      >
        {/* Hero media: poster image is always rendered for LCP; video mounts
            on top only when its URL is reachable. */}
        <HeroMedia
          posterSrc={ranchStreet}
          videoSrc={heroVideoUrl}
          className="absolute inset-0 w-full h-full object-cover scale-110"
        />
      </div>
      
      {/* Overlay with subtle gradient */}
      <div className="absolute inset-0 bg-hero-overlay" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/20" />
      
      {/* Content with counter-parallax for depth */}
      <div 
        className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        style={{
          transform: `translate3d(0, ${counterParallaxOffset}px, 0)`,
        }}
      >
        <div className="max-w-4xl mx-auto">
          {/* Main Title — rendered opaque immediately so it qualifies as
              the LCP candidate without waiting for a fade-in animation.
              (Subtitle and CTAs below keep their decorative fade-ins.) */}
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal text-hero-text leading-tight tracking-wide mb-4">
            Y.O. Ranch Headquarters
          </h1>
          
          {/* Subtitle */}
          <div className="flex items-center justify-center gap-4 mb-6 opacity-0 animate-fade-in" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
            <span className="w-12 h-px bg-hero-text/40" />
            <span className="font-sans text-sm tracking-ultrawide uppercase text-hero-text/80">
              Texas History Museum
            </span>
            <span className="w-12 h-px bg-hero-text/40" />
          </div>
          
          {/* Subtitle */}
          <p className="mt-6 font-serif text-xl sm:text-2xl md:text-3xl text-hero-text/90 italic font-normal opacity-0 animate-fade-in" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
            A Historic Texas Hill Country Experience
          </p>
        </div>
      </div>
      
      {/* Scroll Indicator */}
      <div className="absolute bottom-28 sm:bottom-24 left-1/2 -translate-x-1/2 z-10 opacity-0 animate-fade-in" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
        <div className="flex flex-col items-center gap-2 animate-bounce-gentle">
          <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-hero-text/40">Scroll</span>
          <svg width="16" height="24" viewBox="0 0 16 24" fill="none" className="text-hero-text/30">
            <path d="M8 4L8 18M8 18L2 12M8 18L14 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {/* Bottom CTA Bar */}
      <div className="absolute bottom-0 left-0 right-0 opacity-0 animate-fade-in" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
        <div className="flex flex-col sm:flex-row">
          {/* Plan Your Visit CTA */}
          <Link 
            to="/visit" 
            className="flex-1 bg-hero/90 backdrop-blur-sm border-t border-r border-hero-text/10 px-8 py-6 group transition-all duration-300 hover:bg-hero"
          >
            <div className="container mx-auto flex items-center justify-between max-w-md">
              <span className="font-sans text-sm tracking-widest uppercase text-hero-text">
                Plan Your Visit
              </span>
              <ArrowRight className="w-5 h-5 text-hero-text transition-transform duration-300 group-hover:translate-x-2" />
            </div>
          </Link>
          
          {/* Vertical Divider - Desktop Only */}
          <div className="hidden sm:flex items-center justify-center w-px">
            <div className="h-8 w-px bg-hero-text/20" />
          </div>
          
          {/* Explore Exhibits CTA */}
          <Link 
            to="/explore" 
            className="flex-1 bg-hero/90 backdrop-blur-sm border-t border-hero-text/10 px-8 py-6 group transition-all duration-300 hover:bg-hero"
          >
            <div className="container mx-auto flex items-center justify-between max-w-md">
              <span className="font-sans text-sm tracking-widest uppercase text-hero-text">
                Explore Exhibits
              </span>
              <ArrowRight className="w-5 h-5 text-hero-text transition-transform duration-300 group-hover:translate-x-2" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;